/**
 * Which forms a past operational period actually holds, for the Approved ICS Forms picker.
 *
 * Approved ICS Forms browses what was authored, so an empty form is not worth offering. Existence is read from the
 * same three version sources the Planning P rail uses, via `buildStepperFormStatuses`, rather than a fourth query
 * shaped just for this page.
 */

import { ICS_201_CARD_OPTION, type CardOption } from './cardOptions';
import {
  buildStepperFormStatuses,
  type BuildStepperFormStatusesInput,
} from './stepperFormStatus';
import { getIncidentCardTitle, WIDGET_TITLES } from './USCGPage';

/**
 * The assembled package, always offered.
 *
 * It is not a versioned form of its own — it collates the ones that are — so it has no tip to look for and never
 * drops out of the list.
 */
export const APPROVED_FORMS_IAP_WIDGET = 'incidentActionPlan';

/**
 * Period-scoped existence only: the `ics202` completeness input is deliberately left out, so the 202 is judged by
 * whether it has a tip rather than by how full the live incident record is.
 */
export function buildApprovedFormOptions(
  input: Omit<BuildStepperFormStatusesInput, 'ics202'>
): CardOption[] {
  const statuses = buildStepperFormStatuses(input);
  const hasVersion = (widgetName: string) =>
    (statuses[widgetName]?.status ?? 'empty') !== 'empty';

  const options: CardOption[] = [
    {
      value: APPROVED_FORMS_IAP_WIDGET,
      label: getIncidentCardTitle(APPROVED_FORMS_IAP_WIDGET),
    },
  ];

  // The 201 leads the forms: it is the briefing the rest of the package grows out of, and it is absent from
  // WIDGET_TITLES under this name.
  if (hasVersion(ICS_201_CARD_OPTION.value)) {
    options.push(ICS_201_CARD_OPTION);
  }

  for (const { widgetName, title } of WIDGET_TITLES) {
    if (widgetName === APPROVED_FORMS_IAP_WIDGET) continue;
    if (!hasVersion(widgetName)) continue;
    options.push({ value: widgetName, label: title });
  }

  return options;
}
